import { useCallback } from "react";

import { type AssetType } from "@doitintl/cmp-models";
import { v4 as uuidv4 } from "uuid";

import { type AssetDescriptor, type BillingProfileInvoiceBucket } from "../../../BillingProfileForm.models";

export function useBucketsCRUD(
  buckets: BillingProfileInvoiceBucket[],
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
) {
  return {
    setBucketAsDefault: useCallback(
      (newDefaultBucket: BillingProfileInvoiceBucket) => {
        setFieldValue(
          "buckets",
          buckets.map((bucket) => {
            if (bucket === newDefaultBucket) {
              return {
                ...bucket,
                isDefault: true,
              };
            }

            if (bucket.type === newDefaultBucket.type) {
              return {
                ...bucket,
                isDefault: false,
              };
            }

            return bucket;
          })
        );
      },
      [buckets, setFieldValue]
    ),

    setBucketName: useCallback(
      (bucketToRename: BillingProfileInvoiceBucket, name: string) => {
        setFieldValue(
          "buckets",
          buckets.map((bucket) => (bucket === bucketToRename ? { ...bucketToRename, name } : bucket))
        );
      },
      [buckets, setFieldValue]
    ),

    createBucket: useCallback(
      ({ name, type, isDefault }: { name: string; type: AssetType; isDefault: boolean }) => {
        const newBucket: BillingProfileInvoiceBucket = { _id: uuidv4(), name, assets: [], isDefault, type };

        setFieldValue("buckets", [
          ...buckets.map((existingBucket) => {
            if (newBucket.isDefault && existingBucket.type === newBucket.type) {
              return {
                ...existingBucket,
                isDefault: false,
              };
            }
            return existingBucket;
          }),
          newBucket,
        ]);
      },
      [buckets, setFieldValue]
    ),

    deleteBucket: useCallback(
      (bucketToDelete: BillingProfileInvoiceBucket, newDefaultBucket?: BillingProfileInvoiceBucket) => {
        setFieldValue(
          "buckets",
          buckets
            .filter((bucket) => bucket !== bucketToDelete)
            .map((bucket) => {
              if (bucket === newDefaultBucket) {
                return {
                  ...bucket,
                  isDefault: true,
                };
              }
              return bucket;
            })
        );
      },
      [buckets, setFieldValue]
    ),

    assignAssetsToBucket: useCallback(
      (bucketToAssignTo: BillingProfileInvoiceBucket, assets: AssetDescriptor[]) => {
        setFieldValue(
          "buckets",
          buckets.map((bucket) => (bucket === bucketToAssignTo ? { ...bucket, assets } : bucket))
        );
      },
      [buckets, setFieldValue]
    ),
  };
}
